import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, CreditCard, Wallet, AlertTriangle, ShoppingBag } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import InstrumentSVG from "@/components/InstrumentSVG";
import { useCart } from "@/context/CartContext";
import publicApi from "@/lib/publicApi";
import { readError } from "@/lib/api";
import { parseLocaleFromPath, localePath } from "@/lib/locale";
import { formatEurPlain } from "@/lib/money";

const L = {
  hr: { title: "Plaćanje", subtitle: "Provjerite svoje podatke prije sigurnog plaćanja.", contact: "Kontakt podaci", name: "Ime i prezime", email: "E-mail", phone: "Telefon", shipping: "Adresa za dostavu", street: "Ulica i broj", city: "Grad", postal: "Poštanski broj", country: "Država", method: "Način plaćanja", stripe_label: "Kartično plaćanje (Stripe)", stripe_help: "Sigurno plaćanje karticom — Stripe.", mypos_label: "MyPOS (u razvoju)", mypos_help: "Ova metoda još nije aktivna.", summary: "Pregled narudžbe", total: "Ukupno", pay: "Plati sigurno", processing: "Obrada…", empty: "Vaša košarica je prazna.", back: "Natrag", mypos_warning: "⚠ MyPOS integracija je u razvoju. Za sada koristite Stripe.", mypos_dev_msg: "Hvala — MyPOS će uskoro biti omogućen. Za sada koristite Stripe.", error: "Plaćanje nije uspjelo." },
  en: { title: "Checkout", subtitle: "Confirm your details before secure payment.", contact: "Contact details", name: "Full name", email: "Email", phone: "Phone", shipping: "Shipping address", street: "Street", city: "City", postal: "Postal code", country: "Country", method: "Payment method", stripe_label: "Card (Stripe)", stripe_help: "Secure card payment — Stripe.", mypos_label: "MyPOS (in development)", mypos_help: "This method is not yet active.", summary: "Order summary", total: "Total", pay: "Pay securely", processing: "Processing…", empty: "Your cart is empty.", back: "Back", mypos_warning: "⚠ MyPOS integration is in development. Please use Stripe for now.", mypos_dev_msg: "Thanks — MyPOS will be enabled soon. Please use Stripe for now.", error: "Payment failed." },
  de: { title: "Kasse", subtitle: "Überprüfen Sie Ihre Daten vor der sicheren Zahlung.", contact: "Kontaktdaten", name: "Vollständiger Name", email: "E-Mail", phone: "Telefon", shipping: "Lieferadresse", street: "Straße", city: "Stadt", postal: "Postleitzahl", country: "Land", method: "Zahlungsmethode", stripe_label: "Karte (Stripe)", stripe_help: "Sichere Kartenzahlung — Stripe.", mypos_label: "MyPOS (in Entwicklung)", mypos_help: "Diese Methode ist noch nicht aktiv.", summary: "Bestellübersicht", total: "Gesamt", pay: "Sicher zahlen", processing: "Verarbeitung…", empty: "Ihr Warenkorb ist leer.", back: "Zurück", mypos_warning: "⚠ MyPOS-Integration in Entwicklung. Bitte vorerst Stripe verwenden.", mypos_dev_msg: "Danke — MyPOS wird bald verfügbar sein. Bitte vorerst Stripe verwenden.", error: "Zahlung fehlgeschlagen." },
  fr: { title: "Paiement", subtitle: "Confirmez vos détails avant le paiement sécurisé.", contact: "Coordonnées", name: "Nom complet", email: "E-mail", phone: "Téléphone", shipping: "Adresse de livraison", street: "Rue", city: "Ville", postal: "Code postal", country: "Pays", method: "Mode de paiement", stripe_label: "Carte (Stripe)", stripe_help: "Paiement sécurisé par carte — Stripe.", mypos_label: "MyPOS (en développement)", mypos_help: "Cette méthode n'est pas encore active.", summary: "Récapitulatif", total: "Total", pay: "Payer en sécurité", processing: "Traitement…", empty: "Votre panier est vide.", back: "Retour", mypos_warning: "⚠ L'intégration MyPOS est en développement. Veuillez utiliser Stripe.", mypos_dev_msg: "Merci — MyPOS sera bientôt disponible. Veuillez utiliser Stripe.", error: "Échec du paiement." },
};

export default function Checkout() {
  const navigate = useNavigate();
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const t = L[locale] || L.hr;
  const { items, total } = useCart();

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [shipping, setShipping] = useState({ street: "", city: "", postal: "", country: "HR" });
  const [provider, setProvider] = useState("stripe");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [myposMessage, setMyposMessage] = useState("");

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-20 text-center">
        <ShoppingBag className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.2} />
        <h1 className="font-serif text-3xl text-foreground">{t.empty}</h1>
        <Link to={localePath(locale, "/models")} className="mt-6 inline-block">
          <Button variant="outline">← {t.back}</Button>
        </Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMyposMessage("");
    setBusy(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_slug: it.product_slug,
          product_name: typeof it.product_name === "string" ? it.product_name : (it.product_name?.[locale] || it.product_name?.hr || ""),
          unit_price_eur: it.unit_price_eur,
          qty: 1,
          configuration: it.configuration || [],
        })),
        customer: { ...customer, preferred_contact: "email" },
        shipping_address: shipping,
        origin_url: window.location.origin,
        locale,
        provider,
      };
      const endpoint = provider === "stripe" ? "/checkout/session" : "/checkout/mypos-session";
      const { data } = await publicApi.post(endpoint, payload);

      if (data.mocked) {
        setMyposMessage(data.message || t.mypos_dev_msg);
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setError(t.error);
    } catch (err) {
      setError(readError(err, t.error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title={`${t.title} — Tambure Žmegač`} description={t.subtitle} />
      <section className="bg-background grain-bg">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
          <Link to={localePath(locale, "/cart")} data-testid="checkout-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.back}
          </Link>
          <h1 className="font-serif text-5xl text-foreground leading-tight">{t.title}</h1>
          <p className="mt-2 text-base text-foreground/70">{t.subtitle}</p>

          <form onSubmit={submit} className="mt-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start" data-testid="checkout-form">
            <div className="space-y-6">
              <Card className="bg-card border-border/60 shadow-ambient">
                <CardContent className="p-6 space-y-4">
                  <h2 className="font-serif text-xl">{t.contact}</h2>
                  <div className="space-y-2">
                    <Label className="text-xs">{t.name}</Label>
                    <Input required value={customer.name} onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} className="h-11 bg-background" data-testid="checkout-name" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">{t.email}</Label>
                      <Input required type="email" value={customer.email} onChange={(e) => setCustomer((c) => ({ ...c, email: e.target.value }))} className="h-11 bg-background" data-testid="checkout-email" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">{t.phone}</Label>
                      <Input value={customer.phone} onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} className="h-11 bg-background" data-testid="checkout-phone" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border/60 shadow-ambient">
                <CardContent className="p-6 space-y-4">
                  <h2 className="font-serif text-xl">{t.shipping}</h2>
                  <div className="space-y-2">
                    <Label className="text-xs">{t.street}</Label>
                    <Input required value={shipping.street} onChange={(e) => setShipping((s) => ({ ...s, street: e.target.value }))} className="h-11 bg-background" data-testid="checkout-street" />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="text-xs">{t.city}</Label>
                      <Input required value={shipping.city} onChange={(e) => setShipping((s) => ({ ...s, city: e.target.value }))} className="h-11 bg-background" data-testid="checkout-city" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">{t.postal}</Label>
                      <Input required value={shipping.postal} onChange={(e) => setShipping((s) => ({ ...s, postal: e.target.value }))} className="h-11 bg-background" data-testid="checkout-postal" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t.country}</Label>
                    <Input required value={shipping.country} onChange={(e) => setShipping((s) => ({ ...s, country: e.target.value }))} className="h-11 bg-background" data-testid="checkout-country" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border/60 shadow-ambient">
                <CardContent className="p-6 space-y-4">
                  <h2 className="font-serif text-xl">{t.method}</h2>
                  <RadioGroup value={provider} onValueChange={setProvider} className="space-y-2">
                    <label className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${provider === "stripe" ? "border-primary bg-primary/5" : "border-border/60"}`} data-testid="provider-stripe">
                      <RadioGroupItem value="stripe" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <CreditCard className="h-4 w-4" strokeWidth={1.5} /> {t.stripe_label}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{t.stripe_help}</p>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${provider === "mypos" ? "border-secondary bg-secondary/5" : "border-border/60"}`} data-testid="provider-mypos">
                      <RadioGroupItem value="mypos" className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <Wallet className="h-4 w-4" strokeWidth={1.5} /> {t.mypos_label}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{t.mypos_help}</p>
                      </div>
                    </label>
                  </RadioGroup>
                  {provider === "mypos" && (
                    <div data-testid="mypos-banner" className="flex items-start gap-2 rounded-lg bg-secondary/15 border border-secondary/30 px-3 py-2.5 text-xs text-foreground/85">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-secondary shrink-0" strokeWidth={1.5} />
                      <span>{t.mypos_warning}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {error && (
                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2" data-testid="checkout-error">
                  <AlertTriangle className="h-4 w-4 mt-0.5" strokeWidth={1.5} /> {error}
                </div>
              )}
              {myposMessage && (
                <div className="rounded-lg border border-secondary/30 bg-secondary/10 p-4 text-sm text-foreground" data-testid="mypos-response">
                  {myposMessage}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-24">
              <div className="rounded-lg bg-tz-espresso text-tz-cream shadow-ambient p-6">
                <h2 className="font-serif text-xl mb-4">{t.summary}</h2>
                <ul className="space-y-3 max-h-72 overflow-y-auto pr-1" data-testid="checkout-summary-items">
                  {items.map((it) => (
                    <li key={it.id} className="flex gap-3 text-sm">
                      <div className="w-12 h-16 shrink-0 rounded-md overflow-hidden bg-tz-cream/5">
                        <InstrumentSVG type={it.product_slug} config={it.preview_config || {}} engravingText={it.engraving_text || ""} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-tz-cream truncate">{typeof it.product_name === "string" ? it.product_name : (it.product_name?.[locale] || it.product_name?.hr)}</div>
                        <div className="text-[11px] text-tz-cream/60">{formatEurPlain(it.unit_price_eur)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-5 border-t border-white/10 flex items-baseline justify-between" data-testid="checkout-total">
                  <span className="uppercase tracking-[0.18em] text-tz-cream/70 text-xs">{t.total}</span>
                  <span className="font-serif text-3xl text-primary">{formatEurPlain(total)}</span>
                </div>
                <Button type="submit" disabled={busy} className="mt-5 w-full h-12 shadow-interactive" data-testid="checkout-pay-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" strokeWidth={1.5} />}
                  {busy ? t.processing : t.pay}
                </Button>
              </div>
            </aside>
          </form>
        </div>
      </section>
    </>
  );
}
